import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /*
   * Phone mode (Luxe Confetti, 2026-10-06). On phones the stock rule ("show once the buy
   * buttons have scrolled off the top, hide again near the footer") never fired: there's only
   * ~200px of content between the buttons and the footer, so the bar hid the instant it would
   * have shown. On phones the bar instead shows whenever the real Add to cart is off-screen
   * (above or below), and hides while a text field has focus (keyboard open) or a cookie banner
   * is showing. Desktop keeps the stock behaviour untouched.
   */
  /** @type {MediaQueryList} */
  #phoneQuery = window.matchMedia('(max-width: 749px)');

  /** @type {boolean} */
  #buyButtonsInView = true;

  /** @type {boolean} */
  #isTyping = false;

  /** @type {MutationObserver | null} */
  #estimatedTotalObserver = null;

  /** @type {MutationObserver | null} */
  #consentBannerObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });

    // Phone mode wiring (see #phoneQuery).
    document.addEventListener('focusin', this.#handleFocusChange, { signal });
    document.addEventListener('focusout', this.#handleFocusChange, { signal });
    document.addEventListener('click', this.#handleDocumentClick, { signal, capture: true });
    this.#phoneQuery.addEventListener('change', this.#handlePhoneQueryChange, { signal });
    this.#consentBannerObserver = new MutationObserver(() => this.#updatePhoneBar());
    this.#consentBannerObserver.observe(document.body, { childList: true });
    this.toggleAttribute('data-phone-mode', this.#phoneQuery.matches);

    this.#watchEstimatedTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#estimatedTotalObserver?.disconnect();
    this.#consentBannerObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      this.#buyButtonsInView = entry.isIntersecting;
      if (this.#phoneQuery.matches) {
        this.#updatePhoneBar();
        return;
      }

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        // Phone mode doesn't hide near the footer; the page gets bottom padding instead so the
        // footer's last lines can scroll clear of the bar (see the section stylesheet).
        if (this.#phoneQuery.matches) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Required personalisation still empty: take the shopper to the first missing field (the
    // "Still needed" checklist updates too) instead of clicking a disabled button for nothing.
    const section = this.closest('.shopify-section');
    const personalizations = section?.querySelectorAll('product-personalization-component') ?? [];
    for (const component of personalizations) {
      const revealer = /** @type {{ revealFirstMissingField?: () => boolean }} */ (/** @type {unknown} */ (component));
      if (revealer.revealFirstMissingField?.()) {
        this.#flashMissingNotice(component);
        return;
      }
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
        this.#syncPriceWithEstimatedTotal();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /** @type {number | undefined} */
  #noticeTimeout;

  /**
   * After the scroll to a missing field settles, if the personalisation block's own "Still
   * needed" checklist still isn't on screen (fields and checklist too far apart to fit
   * together), repeat its text just above the bar for a few seconds.
   * @param {Element} component
   */
  #flashMissingNotice(component) {
    const notice = this.querySelector('.sticky-add-to-cart__notice');
    if (!(notice instanceof HTMLElement) || !this.#phoneQuery.matches) return;
    setTimeout(() => {
      const summary = component.querySelector('[ref="validationSummary"]');
      if (!(summary instanceof HTMLElement) || summary.hidden || !summary.textContent?.trim()) return;
      const rect = summary.getBoundingClientRect();
      const barTop = this.refs.stickyBar.getBoundingClientRect().top;
      if (rect.top >= 0 && rect.bottom <= Math.min(window.innerHeight, barTop)) return;
      notice.textContent = summary.textContent.trim();
      notice.hidden = false;
      clearTimeout(this.#noticeTimeout);
      this.#noticeTimeout = setTimeout(() => (notice.hidden = true), 5000);
    }, 700);
  }

  // Phone mode
  /** @param {FocusEvent} event */
  #handleFocusChange = (event) => {
    if (event.type === 'focusin') {
      this.#isTyping = this.#isTextEntry(event.target);
      if (this.#isTyping) this.querySelector('.sticky-add-to-cart__notice')?.setAttribute('hidden', '');
    } else {
      // focusout fires before focus lands on the next element; check where it ended up.
      queueMicrotask(() => {
        this.#isTyping = this.#isTextEntry(document.activeElement);
        this.#updatePhoneBar();
      });
      return;
    }
    this.#updatePhoneBar();
  };

  /** Consent banners can be dismissed without a DOM removal; re-check after any click. */
  #handleDocumentClick = () => {
    if (this.#phoneQuery.matches) requestAnimationFrame(() => this.#updatePhoneBar());
  };

  #handlePhoneQueryChange = () => {
    this.toggleAttribute('data-phone-mode', this.#phoneQuery.matches);
    if (this.#phoneQuery.matches) {
      this.#updatePhoneBar();
    } else {
      // Back to the stock desktop rule: only stuck once the buttons are above the viewport.
      const rect = this.#getProductForm()?.closest('.buy-buttons-block')?.getBoundingClientRect();
      if (rect && rect.bottom < 0 && !this.#isChatActive()) this.#showStickyBar();
      else this.#hideStickyBar();
    }
  };

  /**
   * @param {EventTarget | Element | null} element
   * @returns {boolean}
   */
  #isTextEntry(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element instanceof HTMLTextAreaElement || element.isContentEditable) return true;
    if (element instanceof HTMLSelectElement) return true;
    if (!(element instanceof HTMLInputElement)) return false;
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image', 'hidden'].includes(
      element.type
    );
  }

  /** @returns {boolean} */
  #isConsentBannerShowing() {
    const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner__dialog, [id^="shopify-pc__banner"]');
    if (!(banner instanceof HTMLElement)) return false;
    const style = getComputedStyle(banner);
    const rect = banner.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0 && rect.width > 0;
  }

  #updatePhoneBar() {
    if (!this.#phoneQuery.matches) return;
    const show =
      !this.#buyButtonsInView && !this.#isTyping && !this.#isConsentBannerShowing() && !this.#isChatActive();
    if (show === this.#isStuck) return;
    if (show) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  /**
   * Keeps the bar's price equal to the personalisation block's "Estimated total" (base price
   * plus any paid add-ons the shopper has switched on). Products without that line keep the
   * variant price the server rendered.
   */
  #watchEstimatedTotal() {
    const total = this.#getEstimatedTotal();
    if (!total) return;
    this.#estimatedTotalObserver = new MutationObserver(() => this.#syncPriceWithEstimatedTotal());
    this.#estimatedTotalObserver.observe(total, { childList: true, characterData: true, subtree: true });
    this.#syncPriceWithEstimatedTotal();
  }

  /** @returns {HTMLElement | null} */
  #getEstimatedTotal() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('product-personalization-component [ref="estimatedTotal"]') ?? null;
  }

  #syncPriceWithEstimatedTotal() {
    const text = this.#getEstimatedTotal()?.textContent?.trim();
    if (!text) return;
    for (const price of this.querySelectorAll('[data-sticky-price]')) {
      if (price.textContent?.trim() !== text) price.textContent = text;
    }
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
